'use client';
import { useMemo } from 'react';

import { usePricesHistory } from '@/services/query/marketdetails.queries';
import { EventMarket, PricesHistoryintervalTypes } from '@/types';
import { getClobTokenId } from '@/utils/marketUtils';

import { MarketTimeRange } from '@/constants';

export const useHistoricalChartData = (
  topMarkets: EventMarket[],
  selectedRange: MarketTimeRange,
) => {
  const apiInterval = useMemo(() => {
    switch (selectedRange) {
      case '1H':
        return '1h';
      case '6H':
        return '6h';
      case '1D':
        return '1d';
      case '1W':
        return '1w';
      case '1M':
        return '1m';
      default:
        return 'max' as PricesHistoryintervalTypes;
    }
  }, [selectedRange]);

  const pricesHistory1 = usePricesHistory({
    market: getClobTokenId(topMarkets[0]),
    interval: apiInterval,
    fidelity: 60,
  });

  const pricesHistory2 = usePricesHistory({
    market: getClobTokenId(topMarkets[1]),
    interval: apiInterval,
    fidelity: 60,
  });

  const pricesHistory3 = usePricesHistory({
    market: getClobTokenId(topMarkets[2]),
    interval: apiInterval,
    fidelity: 60,
  });

  const pricesHistoryQueries = useMemo(
    () =>
      [pricesHistory1, pricesHistory2, pricesHistory3].filter(
        (_, idx) => topMarkets[idx] !== undefined,
      ),
    [pricesHistory1, pricesHistory2, pricesHistory3, topMarkets],
  );

  const isChartLoading = pricesHistoryQueries.some((q) => q.isLoading);

  const historicalData = useMemo(() => {
    if (pricesHistoryQueries.length === 0 || topMarkets.length === 0) return [];

    const uniqueTimestamps = new Set<number>();
    const marketDataMaps: Map<number, number>[] = [];

    pricesHistoryQueries.forEach((query) => {
      const map = new Map<number, number>();
      if (Array.isArray(query.data)) {
        query.data.forEach((point: any) => {
          const ts = point.time || point.timestamp;
          if (ts && typeof ts === 'number' && !isNaN(ts)) {
            uniqueTimestamps.add(ts);
            if (point.price !== undefined && point.price !== null) {
              map.set(ts, point.price);
            }
          }
        });
      }
      marketDataMaps.push(map);
    });

    const sortedTimestamps = Array.from(uniqueTimestamps).sort((a, b) => a - b);
    const lastKnownPrices: number[] = new Array(topMarkets.length).fill(-1);

    return sortedTimestamps.map((timestamp) => {
      const point: any = { timestamp };
      topMarkets.forEach((_, index) => {
        const key = `price${index}`;
        const map = marketDataMaps[index];
        const price = map?.get(timestamp);

        if (price !== undefined) {
          point[key] = price;
          lastKnownPrices[index] = price;
        } else if (lastKnownPrices[index] !== -1) {
          point[key] = lastKnownPrices[index];
        } else {
          point[key] = null;
        }
      });
      return point;
    });
  }, [pricesHistoryQueries, topMarkets]);

  return { historicalData, isChartLoading };
};
